import '../css/OnboardingFlow.less';

import React, {useEffect} from 'react';
import {Button, Form, Input, Message} from 'semantic-ui-react';

import OnboardingBackground from '../assets/onboarding/signup-colorful-bkg.svg';
import {doAuthRedirect, LocalAuth} from '../util/auth';
import {useHistory, useLocation} from 'react-router-dom';
import makeComp from '../util/profiler';

interface Props {
  auth?: LocalAuth;
}

const ChangePassword: React.FC<Props> = makeComp(
  ({auth}) => {
    const history = useHistory();
    const location = useLocation();

    const [password, setPassword] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);
    const email = auth?.claims.email;

    // this page is only usable in local auth, and only when a JWT is available
    useEffect(() => {
      if (!auth || !auth.local || !auth.tempKey || !email) {
        console.error(
          'tried to load password reset page with invalid auth or no JWT; redirecting to login'
        );
        history.replace({
          ...location,
          pathname: 'login',
        });
      }
    }, [auth, history, location, email]);

    useEffect(() => {
      if (['models', 'local'].indexOf(auth?.claims.email) !== -1) {
        console.error(
          'tried to load password reset page for nonexistent user; redirecting to signup'
        );

        history.replace({
          ...location,
          pathname: 'signup',
        });
      }
    }, [history, location, auth]);

    const submit = async () => {
      if (password.length < 5) {
        return;
      }

      setError(null);

      if (!auth || !email) {
        setError('Error while changing password.');
        throw new Error(
          'Somehow submitted password reset in invalid state: this page should not load at all in these conditions'
        );
      }

      try {
        await auth.changePassword(password, email);
        doAuthRedirect();
      } catch (e) {
        console.error('Error changing password: ', e);
        setError('Error while changing password');
      }
    };

    return (
      <div
        className="onboarding-flow"
        style={{backgroundImage: `url(${OnboardingBackground})`}}>
        <div className="onboarding-dialogue">
          <h2>Change Password</h2>
          <Form onSubmit={submit}>
            <p>Enter a new password for the account associated with {email}</p>
            <Message error visible={!!error}>
              {error}
            </Message>
            <Form.Field
              control={Input}
              type="password"
              autoComplete="new-password"
              data-test="new-password"
              value={password}
              onChange={(e: any) => {
                setPassword(e.target.value);
              }}
              error={
                password !== '' &&
                password.length < 5 &&
                'Password of at least 5 characters required'
              }
              placeholder="Password"
            />
            <Button type="submit" style={{display: 'none'}}>
              Change Password
            </Button>
          </Form>
          <div className="actions">
            <Button
              data-test="submit"
              primary
              onClick={submit}
              disabled={password.length < 5}>
              Change Password
            </Button>
          </div>
        </div>
      </div>
    );
  },
  {id: 'ChangePassword'}
);

export default ChangePassword;
